import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, HelpCircle } from "lucide-react";
import { useMemo } from "react";
import {
  useCreateServiceStandard,
  useServiceStandardQuery,
} from "../api/queries";
import {
  ServiceStandardFormTour,
  useServiceStandardFormTour,
} from "../tour/ServiceStandardFormTour";
import type { ServiceStandardFormValues } from "../ui/ServiceStandardForm";
import { ServiceStandardForm } from "../ui/ServiceStandardForm";

export function ServiceStandardCreatePage() {
  const navigate = useNavigate({ from: "/app/service-standard/new" });
  const search = useSearch({ strict: false });
  const createServiceStandard = useCreateServiceStandard();
  const tour = useServiceStandardFormTour();

  const copyFromId = (search as { copyFrom?: string })?.copyFrom;
  const sourceStandard = useServiceStandardQuery(copyFromId || "");

  const initialValues = useMemo<
    Partial<ServiceStandardFormValues> | undefined
  >(() => {
    if (!sourceStandard.data || !copyFromId || !sourceStandard.isSuccess)
      return undefined;

    const standard = sourceStandard.data;
    return {
      name: `${standard.name} (ສໍາເນົາ)`,
      description: standard.description,
      serviceChannelId: standard.serviceChannelId,
      departmentId: standard.departmentId ?? null,
      positionId: standard.positionId ?? null,
      serviceStage: standard.serviceStage,
      status: "draft",
      items:
        standard.items?.map((item) => ({
          id: item.id,
          itemType: item.itemType,
          content: item.content,
          description: item.description,
          orderIndex: item.orderIndex,
        })) ?? [],
      examples:
        standard.examples?.map((ex) => ({
          id: ex.id,
          situation: ex.situation,
          example: ex.example,
          description: ex.description,
          orderIndex: ex.orderIndex,
        })) ?? [],
      specialCases:
        standard.specialCases?.map((sc) => ({
          id: sc.id,
          title: sc.title,
          handling: sc.handling,
          description: sc.description,
          orderIndex: sc.orderIndex,
        })) ?? [],
    };
  }, [sourceStandard.data, sourceStandard.isSuccess, copyFromId]);

  return (
    <>
      <Header />

      <Main>
        <ServiceStandardFormTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div className="space-y-6">
          {/* Header Section */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-bold text-2xl">ສ້າງມາດຕະຖານບໍລິການໃໝ່</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="mt-1 text-muted-foreground">
                ສ້າງມາດຕະຖານບໍລິການໃໝ່ສໍາລັບການນໍາໃຊ້
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  navigate({ to: "/app/service-standard" });
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                ກັບ
              </Button>
            </div>
          </div>

          {/* Create Form Section */}
          <div className="rounded-xl border bg-card p-6">
            <ServiceStandardForm
              initialValues={initialValues}
              onSubmit={async (vals) => {
                await createServiceStandard.mutateAsync({
                  name: vals.name,
                  description: vals.description ?? undefined,
                  serviceChannelId: vals.serviceChannelId,
                  departmentId: vals.departmentId ?? undefined,
                  positionId: vals.positionId ?? undefined,
                  serviceStage: vals.serviceStage,
                  status: vals.status,
                  items: vals.items?.map((item, index) => ({
                    ...item,
                    orderIndex: item.orderIndex ?? index,
                  })),
                  examples: vals.examples?.map((ex, index) => ({
                    ...ex,
                    orderIndex: ex.orderIndex ?? index,
                  })),
                  specialCases: vals.specialCases?.map((sc, index) => ({
                    ...sc,
                    orderIndex: sc.orderIndex ?? index,
                  })),
                });
                navigate({ to: "/app/service-standard" });
              }}
              submitting={createServiceStandard.isPending}
            />
          </div>
        </div>
      </Main>
    </>
  );
}
